import styled from "styled-components";
import React, { useState, useEffect, useCallback } from "react";
import BackgroundBlock from "./BackgroundBlock";
import { HomeHeader } from "./HomeHeader";
import { useAuth } from "../../contexts/AuthContext";
import { fetchCoursesByEmailFromFirestore } from '../../admin';
import { getDraft } from "../../firebase";
import dayjs from "dayjs";
import { useNavigate, useLocation } from "react-router-dom";
import { FiAlertTriangle, FiArrowLeft } from "react-icons/fi";
import useBodyScrollLock from "../../hooks/useBodyScrollLock";
import { getCourseTimeZone } from "../../utils/courseSchedule";

const toDayjs = (d) => {
    if (!d) return null;
    if (typeof d?.toDate === 'function') return dayjs(d.toDate()); // Firestore Timestamp or Dayjs-like
    return dayjs(d); // Date or ISO string
};

/** Course allows Discord artifacts (defaults true if unset). */
const courseHasDiscordEnabled = (course) =>
    course?.settings?.artifacts?.discordEnabled !== false;

const getProgressPct = (start, end) => {
    if (!start || !end) return null;
    const now = dayjs();

    const total = end.diff(start);   // ms
    if (total <= 0) return now.isAfter(end) ? 100 : 0;

    const elapsed = Math.min(Math.max(now.diff(start), 0), total);
    return Math.round((elapsed / total) * 100);
};

const StudentHome = () => {
    const { user, setGroupInfo, setCourseInfo, setIsView } = useAuth();
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();
    
    const discordClaimParam = location.search ? new URLSearchParams(location.search).get("discordClaim") : null;
    const [claimLoading, setClaimLoading] = useState(!!discordClaimParam);
    const [claimError, setClaimError] = useState(null);
    const [showCoursePopup, setShowCoursePopup] = useState(false);
    const [selectedGroupIdForClaim, setSelectedGroupIdForClaim] = useState("");

    useBodyScrollLock(showCoursePopup && courses.length >= 2);

    useEffect(() => {
        if (!discordClaimParam || !user?.email) return;
        let cancelled = false;
        setClaimLoading(true);
        setClaimError(null);
        getDraft(discordClaimParam)
            .then((draft) => {
                if (cancelled) return;
                if (!draft) {
                    setClaimError("Draft not found. It may have expired or the link is invalid.");
                } else if (draft.claimed) {
                    setClaimError("This draft has already been claimed.");
                } else {
                    setClaimError(null);
                }
            })
            .catch(() => {
                if (!cancelled) setClaimError("Failed to load draft. Try again.");
            })
            .finally(() => {
                if (!cancelled) setClaimLoading(false);
            });
        return () => { cancelled = true; };
    }, [discordClaimParam, user?.email]);

    useEffect(() => {
        if (!discordClaimParam || claimLoading || claimError || loading || !courses.length) return;
        const eligible = courses.filter(courseHasDiscordEnabled);
        console.log("eligible:", eligible);

        // no eligible at all → error
        if (eligible.length === 0) {
            setClaimError(
                "Discord is not enabled for your course(s). Ask your instructor to turn on Discord in course integration settings."
            );
            return;
        }

        // ONLY ONE COURSE TOTAL → auto jump
        if (courses.length === 1) {
            const course = courses[0];

            if (!courseHasDiscordEnabled(course)) {
                setClaimError(
                  "Discord is not enabled for your course."
                );
                return;
            }

            setGroupInfo({
                groupId: course.groupId,
                name: course.name,
                startDate: course.startDate,
                endDate: course.endDate ?? null,
            });
            setCourseInfo({
                courseId: course.courseId,
            });
            setIsView(false);
            navigate(`/activities?discordClaim=${encodeURIComponent(discordClaimParam)}`, { replace: true });
            return;
        }

        // multiple courses → ALWAYS let user choose
        setShowCoursePopup(true);
        setSelectedGroupIdForClaim((prev) => {
            const ok = courses.some((c) => c.groupId === prev);
            return ok ? prev : courses[0].groupId;
        });
    }, [discordClaimParam, claimLoading, claimError, loading, courses, setGroupInfo, setCourseInfo, setIsView, navigate]);

    useEffect(() => {
        if (
            showCoursePopup &&
            courses.length >= 2 &&
            !courses.some((c) => c.groupId === selectedGroupIdForClaim) &&
            courses[0]
        ) {
            setSelectedGroupIdForClaim(courses[0].groupId);
        }
    }, [showCoursePopup, courses, selectedGroupIdForClaim]);

    const handleClaimContinue = useCallback(() => {
        if (!selectedGroupIdForClaim || !discordClaimParam) return;
        const course = courses.find((c) => c.groupId === selectedGroupIdForClaim);
        if (!course || !courseHasDiscordEnabled(course)) {
            setClaimError(
                "Discord is not enabled for the selected course. Choose a course that has Discord enabled."
            );
            setShowCoursePopup(false);
            return;
        }
        setGroupInfo({
            groupId: course.groupId,
            name: course.name,
            startDate: course.startDate,
            endDate: course.endDate ?? null,
        });
        setCourseInfo({
            courseId: course.courseId,
        });
        setIsView(false);
        setShowCoursePopup(false);
        navigate(`/activities?discordClaim=${encodeURIComponent(discordClaimParam)}`, { replace: true });
    }, [selectedGroupIdForClaim, discordClaimParam, courses, setGroupInfo, setCourseInfo, setIsView, navigate]);

    const clearClaimParam = useCallback(() => {
        const params = new URLSearchParams(location.search);
        params.delete("discordClaim");
        const qs = params.toString();
        navigate(qs ? `${location.pathname}?${qs}` : location.pathname, { replace: true });
        setClaimError(null);
        setShowCoursePopup(false);
    }, [location, navigate]);

    const handleNavigateToDetail = (course) => {
        const groupInfo = {
            groupId: course.groupId,
            name: course.name,
            startDate: course.startDate,
            endDate: course.endDate || null,
            artifacts: course.settings?.artifacts
        };
        setGroupInfo(groupInfo);
        setCourseInfo({
            courseId: course.courseId,
        });
        setIsView(false);
        navigate(`/activities`);
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const fetchedCourses = await fetchCoursesByEmailFromFirestore(user.email);
                setCourses(fetchedCourses);
            } catch (error) {
                console.error("Error fetching courses:", error);
            } finally {
                setLoading(false);
            }
        };

        if (user && user.email) {
            fetchData();
        }
    }, [user]);

    useEffect(() => {
        if (!discordClaimParam) setGroupInfo("");
    }, [location, setGroupInfo, discordClaimParam]);

    return (
        <>
            <HomeHeader text="Dashboard" link="/student/home" />
            <PageWrapper>
                {discordClaimParam && claimLoading && (
                    <ClaimBanner>Preparing to claim Discord activity…</ClaimBanner>
                )}
                {discordClaimParam && claimError && (
                    <ErrorSection>
                    <ErrorContainer>
                      <ErrorLeft>
                        <ErrorIconWrapper>
                          <StyledAlertIcon />
                        </ErrorIconWrapper>
                  
                        <ErrorText>
                          <ErrorTitle>Error</ErrorTitle>
                          <ErrorMessage>{claimError}</ErrorMessage>
                        </ErrorText>
                      </ErrorLeft>
                  
                      <ErrorAction onClick={clearClaimParam}>
                        <StyledArrowIcon />
                        Return to dashboard
                      </ErrorAction>
                    </ErrorContainer>
                  </ErrorSection>
                )}
                {discordClaimParam && !loading && !claimLoading && !claimError && courses.length === 0 && (
                    <ErrorSection>
                    <ErrorContainer>
                      <ErrorLeft>
                        <ErrorIconWrapper>
                          <StyledAlertIcon />
                        </ErrorIconWrapper>
                  
                        <ErrorText>
                          <ErrorTitle>Error</ErrorTitle>
                          <ErrorMessage>You are not in any course. Join a course first.</ErrorMessage>
                        </ErrorText>
                      </ErrorLeft>
                  
                      <ErrorAction onClick={clearClaimParam}>
                        <StyledArrowIcon />
                        Return to dashboard
                      </ErrorAction>
                    </ErrorContainer>
                  </ErrorSection>
                )}
                <BackgroundBlock>
                    {loading ? (
                        <p>Loading...</p>
                    ) : courses.length > 0 ? (
                        <CoursesGrid>
                            {courses.map((course) => {
                                const timeZone = getCourseTimeZone(course);
                                const start = toDayjs(course.startDate)?.tz(timeZone);
                                const end = toDayjs(course.endDate)?.tz(timeZone);
                                const pct = getProgressPct(start, end);
                                const pctSafe = Number.isFinite(pct) ? pct : 0;

                                return (
                                    <Card key={`${course.courseId}-${course.groupId}`}
                                        onClick={() => handleNavigateToDetail(course)}>
                                        <CourseTitle>{course.name || "Unnamed Course"}</CourseTitle>
                                        <CardText>{course.description || course.name}</CardText>

                                        {end ? (
                                            <ProgressBlock>
                                                <ProgressLabels>
                                                    <span title={start?.format('MMM D, YYYY h:mm A z')}>{start ? start.format('YYYY-MM-DD') : '—'}</span>
                                                    <CenterPct>{pctSafe}%</CenterPct>
                                                    <span title={end.format('MMM D, YYYY h:mm A z')}>{end.format('YYYY-MM-DD')}</span>
                                                </ProgressLabels>

                                                <ProgressTrack
                                                    role="progressbar"
                                                    aria-valuemin={0}
                                                    aria-valuemax={100}
                                                    aria-valuenow={pctSafe}
                                                    aria-label="Course progress"
                                                >
                                                    <ProgressFill $pct={pctSafe} />
                                                </ProgressTrack>

                                                <StatusRow>{course.isActive ? 'In Progress' : 'Inactive'}</StatusRow>
                                            </ProgressBlock>
                                        ) : (
                                            <CardBottom>
                                                <MetaRow>{start ? start.format('YYYY-MM-DD') : 'No start date'}</MetaRow>
                                                <MetaRow>{course.isActive ? 'In Progress' : 'Inactive'}</MetaRow>
                                            </CardBottom>
                                        )}
                                    </Card>
                                );
                            })}
                        </CoursesGrid>
                    ) : (
                        <p>No courses found</p>
                    )}
                </BackgroundBlock>
            </PageWrapper>

            {showCoursePopup && courses.length >= 2 && (
                <ClaimOverlay onClick={clearClaimParam}>
                    <ClaimCard onClick={(e) => e.stopPropagation()}>
                        <ClaimCardHeader>
                            <ClaimTitle>Select course</ClaimTitle>
                            <ClaimCloseBtn type="button" onClick={clearClaimParam} aria-label="Cancel">
                                Cancel
                            </ClaimCloseBtn>
                        </ClaimCardHeader>
                        <ClaimSubtitle>Choose which course to add this Discord activity to</ClaimSubtitle>
                        <ClaimLabel htmlFor="claim-course-select">Course</ClaimLabel>
                        <ClaimSelect
                            id="claim-course-select"
                            value={selectedGroupIdForClaim}
                            onChange={(e) => setSelectedGroupIdForClaim(e.target.value)}
                        >
                            {courses.map((c) => (
                                <option key={c.groupId} value={c.groupId}>
                                    {c.name || c.groupId}
                                    {!courseHasDiscordEnabled(c) ? " (Discord disabled)" : ""}
                                </option>
                            ))}
                        </ClaimSelect>
                        <ClaimBtn type="button" onClick={handleClaimContinue}>
                            Continue to activity
                        </ClaimBtn>
                    </ClaimCard>
                </ClaimOverlay>
            )}
        </>
    );
};

const PageWrapper = styled.div`
    background-color: #f8fafc;
    display: flex;
    flex-direction: column;
    padding: 15px 20px;
`;

const CoursesGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    justify-content: flex-start;
`;

const Card = styled.div`
    background-color: white;
    border-radius: 10px;
    padding: 20px;
    width: 300px;
    height: 266px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    position: relative;
    display: flex;
    flex-direction: column;
    cursor: pointer;
    overflow: hidden;
    transform: translateY(0);
    transition: transform .3s ease;

    &:hover { transform: translateY(-4px); } /* hover:-translate-y-1 */
`;

const CourseTitle = styled.h3`
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 0.8rem 0;
    overflow: hidden;
    text-align: left;
    word-break: break-all;
    text-overflow: ellipsis;
    overflow-y: auto;
    overscroll-behavior: contain;
    color: #1f2937;
`;

const CardText = styled.p`
  font-size: 0.875rem;  /* text-sm */
  color: #6b7280;       /* gray-500 */
  margin: 0 0 1rem 0;   /* mb-4 */
  text-align: left;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.875rem;  /* text-sm */
  color: #6b7280;       /* gray-500 */
  gap: 0.25rem;
  font-weight: 600;
`;


const CardBottom = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-top: auto; 
`;

const ProgressBlock = styled.div`
  margin-top: auto;         /* keep at bottom of card */
  width: 100%;
`;

const ProgressLabels = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 6px;
  font-size: 0.825rem;
  color: #6b7280; /* gray-500 */
`;

const CenterPct = styled.span`
  font-weight: 700;
  font-size: 1rem;
  color: #9333ea; /* gray-700 */
`;

const ProgressTrack = styled.div`
  position: relative;
  height: 10px;
  border-radius: 9999px;
  background: #f3f4f6; /* gray-100 */
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${({ $pct = 0 }) => `${Math.max(0, Math.min(100, $pct))}%`};
  background: linear-gradient(90deg, #cea1f6ff, #9333ea);
  border-radius: inherit;
  transition: width .25s ease;
`;


const StatusRow = styled.div`
  margin-top: 6px;
  font-size: 0.825rem;
  color: #6b7280;
  font-weight: 600;
`;

const ClaimBanner = styled.div`
  padding: 12px 16px;
  margin-bottom: 16px;
  background: #eff6ff;
  color:rgb(111, 39, 177);
  font-size: 0.875rem;
  border-radius: 8px;
`;

const ClaimOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ClaimCard = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  padding: 24px;
  max-width: 400px;
  width: 100%;
`;

const ClaimCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const ClaimCloseBtn = styled.button`
  background: none;
  border: none;
  font-size: 0.875rem;
  color: #9333ea;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 8px;
  margin: -4px -8px 0 0;
  &:hover {
    color:rgb(127, 46, 202);
    scale: 1.01;
    transition: scale 0.1s ease;
  }
`;

const ClaimTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const ClaimSubtitle = styled.p`
  font-size: 0.875rem;
  color: #64748b;
  margin: 0 0 16px 0;
`;

const ClaimLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;

  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #64748b;

  text-transform: uppercase;

  margin-bottom: 8px;
`;

const ClaimSelect = styled.select`
  width: 100%;
  padding: 10px 12px;
  font-size: 0.9375rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #fff;
  color: #1e293b;
  cursor: pointer;
  margin-bottom: 16px;
  &:focus {
    outline: none;
    border-color: #9333ea;
    box-shadow: 0 0 0 3px rgba(147, 51, 234, .2);
  }
`;

const ClaimBtn = styled.button`
  width: 100%;
  padding: 10px 16px;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #fff;
  background: #9333ea;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  &:hover { 
  background:rgb(129, 54, 199); 
  scale: 1.01;
  transition: scale 0.1s ease;
  }
`;

const StyledAlertIcon = styled(FiAlertTriangle)`
  width: 20px;
  height: 20px;
  color: #dc2626;
`;

const StyledArrowIcon = styled(FiArrowLeft)`
  width: 16px;
  height: 16px;
  transition: transform 0.2s ease;
`;

const ErrorAction = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;

  background: none;
  border: none;

  font-size: 14px;
  font-weight: 600;
  color: #9333ea;
  cursor: pointer;

  transition: all 0.2s ease;

  &:hover {
    color: #7e22ce;
  }

  &:hover ${StyledArrowIcon} {
    transform: translateX(-3px);
  }
`;

const ErrorSection = styled.section`
  margin-bottom: 20px;
`;

const ErrorContainer = styled.div`
  background: #fef2f2;
  border: 1px solid #fee2e2;
  border-radius: 12px;
  padding: 20px;
  margin: 8px;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;

  box-shadow: 0 1px 2px rgba(0,0,0,0.05);

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
  }
`;

const ErrorLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;

  @media (min-width: 768px) {
    align-items: center;
  }
`;

const ErrorIconWrapper = styled.div`
  background: #fee2e2;
  width: 36px;
  height: 36px;
  border-radius: 50%;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;
`;

const ErrorText = styled.div`
    text-align: left;
`;

const ErrorTitle = styled.h3`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #991b1b;
  margin: 0 0 4px 0;
`;

const ErrorMessage = styled.p`
  font-size: 14px;
  color: #b91c1c;
  font-weight: 500;
  margin: 0;
`;

export default StudentHome;
